import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPost } from '@/lib/api-client';

/**
 * Usage stats schema
 */
const UsageSchema = z.object({
  videosGenerated: z.number(),
  videosLimit: z.number(),
  videosRemaining: z.number(),
  imageAnalyses: z.number(),
  imageAnalysesLimit: z.number(),
  imageAnalysesRemaining: z.number(),
  tokensUsed: z.number(),
  billingPeriodStart: z.string(),
  billingPeriodEnd: z.string(),
  plan: z.enum(['free', 'pro']),
  isCancelling: z.boolean(),
  cancellationDate: z.string().nullable(),
});

export type UsageStats = z.infer<typeof UsageSchema>;

/**
 * Checkout response schema
 */
const CheckoutSchema = z.object({
  url: z.string().url(),
});

/**
 * Hook for getting usage stats
 */
export function useUsage() {
  return useQuery({
    queryKey: ['usage'],
    queryFn: () => apiGet('/billing/usage', UsageSchema),
    refetchInterval: 30000, // Refresh every 30s
    staleTime: 10000, // Consider stale after 10s
  });
}

/**
 * Hook for creating a checkout session
 */
export function useCheckout() {
  return useMutation({
    mutationFn: async () => {
      const result = await apiPost('/billing/checkout', {}, CheckoutSchema);
      return result;
    },
    onSuccess: (data) => {
      // Redirect to Stripe checkout
      if (data.data.url) {
        window.location.href = data.data.url;
      }
    },
  });
}

/**
 * Hook for opening billing portal (general management)
 */
export function useBillingPortal() {
  return useMutation({
    mutationFn: async () => {
      const result = await apiPost('/billing/portal', {}, CheckoutSchema);
      return result;
    },
    onSuccess: (data) => {
      // Redirect to billing portal
      if (data.data.url) {
        window.location.href = data.data.url;
      }
    },
  });
}

/**
 * Hook for cancellation flow (with auto-redirect)
 */
export function useCancelPortal() {
  return useMutation({
    mutationFn: async () => {
      const result = await apiPost('/billing/portal/cancel', {}, CheckoutSchema);
      return result;
    },
    onSuccess: (data) => {
      if (data.data.url) {
        window.location.href = data.data.url;
      }
    },
  });
}

/**
 * Hook to invalidate usage after generation
 */
export function useInvalidateUsage() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['usage'] });
}
